import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  Star,
  Calendar,
  MessageCircle,
  Edit,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Briefcase
} from 'lucide-react';

const GigDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderData, setOrderData] = useState({
    package: 'basic', // 'basic' or 'premium'
    requirements: '',
    budget: '',
    timeline: ''
  });

  // Fetch gig details
  const { data: gig, isLoading, error, refetch } = useQuery(
    ['gig', id],
    () => api.get(API_ENDPOINTS.GIGS.DETAIL(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
    }
  );

  // Refetch gig data when user changes (e.g., role switch)
  useEffect(() => {
    if (user?._id && id) {
      refetch();
    }
  }, [user?._id, user?.role, id, refetch]);

  // Listen for gig updates via socket
  useEffect(() => {
    if (!socket || !id) return;

    socket.on('gig:updated', (updatedGig) => {
      if (updatedGig._id === id) {
        queryClient.setQueryData(['gig', id], { data: updatedGig });
      }
    });

    return () => {
      socket.off('gig:updated');
    };
  }, [socket, id, queryClient]);

  // Check if user has already ordered
  const hasOrdered = gig?.orders?.some(order => order.client.toString() === user?._id);
  const userOrder = gig?.orders?.find(order => order.client.toString() === user?._id);
  const isGigOwner = gig?.talent?._id === user?._id;

  // Order gig mutation
  const orderMutation = useMutation(
    (data) => api.post(API_ENDPOINTS.GIGS.ORDER(id), data),
    {
      onSuccess: () => {
        toast.success('Order placed successfully!');
        queryClient.invalidateQueries(['gig', id]);
        setShowOrderForm(false);
        setOrderData({ package: 'basic', requirements: '', budget: '', timeline: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to place order');
      }
    }
  );

  // Approve order mutation
  const approveOrderMutation = useMutation(
    (orderId) => api.post(API_ENDPOINTS.GIGS.APPROVE_ORDER(id, orderId)),
    {
      onSuccess: (response) => {
        toast.success('Order approved successfully!');
        queryClient.invalidateQueries(['gig', id]);
        // Redirect to chat if chat ID is returned
        if (response.data?.chat) {
          navigate(`/chats/${response.data.chat}`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to approve order');
      }
    }
  );

  // Reject order mutation
  const rejectOrderMutation = useMutation(
    (orderId) => api.post(API_ENDPOINTS.GIGS.REJECT_ORDER(id, orderId)),
    {
      onSuccess: () => {
        toast.success('Order rejected');
        queryClient.invalidateQueries(['gig', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reject order');
      }
    }
  );

  const handleOrder = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to order this gig');
      return;
    }
    orderMutation.mutate(orderData);
  };

  const handleApproveOrder = (orderId) => {
    approveOrderMutation.mutate(orderId);
  };

  const handleRejectOrder = (orderId) => {
    rejectOrderMutation.mutate(orderId);
  };

  const formatPrice = (gig) => {
    if (!gig.pricing) return 'Price negotiable';
    
    // New structure: basic/premium are objects with price property
    if (gig.pricing.basic) {
      const basicPrice = typeof gig.pricing.basic === 'object' && gig.pricing.basic.price 
        ? gig.pricing.basic.price 
        : gig.pricing.basic;
      const premiumPrice = gig.pricing.premium && typeof gig.pricing.premium === 'object' && gig.pricing.premium.price
        ? gig.pricing.premium.price
        : (typeof gig.pricing.premium === 'number' ? gig.pricing.premium : null);
      
      if (premiumPrice) {
        return `$${Number(basicPrice).toFixed(2)} - $${Number(premiumPrice).toFixed(2)}`;
      }
      return `$${Number(basicPrice).toFixed(2)}`;
    }
    
    // Legacy structure: basic/standard/premium are numbers
    if (typeof gig.pricing.basic === 'number') return `$${gig.pricing.basic.toFixed(2)}`;
    if (typeof gig.pricing.standard === 'number') return `$${gig.pricing.standard.toFixed(2)}`;
    if (typeof gig.pricing.premium === 'number') return `$${gig.pricing.premium.toFixed(2)}`;
    
    // Min/max range pricing
    if (gig.pricing.min && gig.pricing.max) {
      return `$${gig.pricing.min.toFixed(2)} - $${gig.pricing.max.toFixed(2)}`;
    }
    if (gig.pricing.min) return `$${gig.pricing.min.toFixed(2)}+`;
    
    return 'Price negotiable';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'in-progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error || !gig) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">😞</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Gig not found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The gig you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/gigs" className="btn-primary">
            Browse Gigs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-accent mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {/* Gig Image */}
        {gig.imageUrl && (
          <div className="mb-6 rounded-lg overflow-hidden">
            <img 
              src={gig.imageUrl} 
              alt={gig.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>
        )}
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-heading mb-2">
              {gig.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
              <div className="flex items-center space-x-1">
                <DollarSign className="w-4 h-4" />
                <span>{formatPrice(gig)}</span>
              </div>
              
              {gig.deliveryTime && gig.deliveryTime.value && gig.deliveryTime.unit && (
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{gig.deliveryTime.value} {gig.deliveryTime.unit}</span>
                </div>
              )}

              {gig.location && (
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span className="whitespace-nowrap">
                    {gig.location.remote && 'Remote'}
                    {gig.location.remote && (gig.location.city || gig.location.country) && ' • '}
                    {(gig.location.city || gig.location.country) && (
                      <>
                        {gig.location.city && gig.location.city}
                        {gig.location.city && gig.location.country && ', '}
                        {gig.location.country && gig.location.country}
                      </>
                    )}
                    {!gig.location.remote && !gig.location.city && !gig.location.country && 'Location not specified'}
                  </span>
                </div>
              )}

              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{gig.orderCount} orders</span>
              </div>

              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span className="whitespace-nowrap">Posted {new Date(gig.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                gig.type === 'professional' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                gig.type === 'labour' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
              }`}>
                {(gig.type ? (gig.type.charAt(0).toUpperCase() + gig.type.slice(1)) : 'Gig')}
              </span>
              {/* Show completed count after type tag */}
              <span className="px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Completed ({gig.orderStatusCounts?.completed || 0})
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 lg:flex-shrink-0">
            {isGigOwner ? (
              <>
                <Link
                  to={`/gigs/${gig._id}/orders`}
                  className="btn-outline"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Orders ({gig.orderCount})
                </Link>
                {/* <Link
                  to={`/gigs/${gig._id}/edit`}
                  className="btn-outline"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Gig
                </Link> */}
              </>
            ) : (
              <>
                {!isAuthenticated ? (
                  <Link to="/login" className="btn-primary">
                    Login to Order
                  </Link>
                ) : hasOrdered ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full text-center sm:text-left ${getStatusColor(userOrder?.status)}`}>
                      {userOrder?.status?.charAt(0).toUpperCase() + userOrder?.status?.slice(1)}
                    </span>
                    {userOrder?.status === 'accepted' && (
                      <Link to="/chats" className="btn-primary text-center">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Contact Talent
                      </Link>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowOrderForm(true)}
                    className="btn-primary"
                  >
                    Order Now
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic & Premium Offers */}
          {gig.pricing && gig.pricing.basic && typeof gig.pricing.basic === 'object' && gig.pricing.basic.price && 
           gig.pricing.premium && typeof gig.pricing.premium === 'object' && gig.pricing.premium.price && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Choose Your Package
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Offer Card */}
                <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:border-accent transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Basic Package
                    </h3>
                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm rounded-full">
                      Standard
                    </span>
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      ${Number(gig.pricing.basic.price).toFixed(2)}
                    </div>
                    {gig.pricing.basic.deliveryTime && (
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{gig.pricing.basic.deliveryTime.value} {gig.pricing.basic.deliveryTime.unit}</span>
                      </div>
                    )}
                  </div>

                  {/* Pros */}
                  {gig.pricing.basic.pros && gig.pricing.basic.pros.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Pros:</h4>
                      <ul className="space-y-1">
                        {gig.pricing.basic.pros.map((pro, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Cons */}
                  {gig.pricing.basic.cons && gig.pricing.basic.cons.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Cons:</h4>
                      <ul className="space-y-1">
                        {gig.pricing.basic.cons.map((con, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                            <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Premium Offer Card */}
                <div className="border-2 border-accent rounded-lg p-6 hover:border-accent-dark transition-colors relative">
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-accent text-white text-xs font-semibold rounded-full">
                      POPULAR
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Premium Package
                    </h3>
                    <span className="px-3 py-1 bg-accent/10 text-accent text-sm rounded-full">
                      Best Value
                    </span>
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      ${Number(gig.pricing.premium.price).toFixed(2)}
                    </div>
                    {gig.pricing.premium.deliveryTime && (
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{gig.pricing.premium.deliveryTime.value} {gig.pricing.premium.deliveryTime.unit}</span>
                      </div>
                    )}
                  </div>

                  {/* Pros */}
                  {gig.pricing.premium.pros && gig.pricing.premium.pros.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Pros:</h4>
                      <ul className="space-y-1">
                        {gig.pricing.premium.pros.map((pro, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Cons */}
                  {gig.pricing.premium.cons && gig.pricing.premium.cons.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Cons:</h4>
                      <ul className="space-y-1">
                        {gig.pricing.premium.cons.map((con, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                            <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Gig Description */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Service Description
            </h2>
            <div 
              className="prose prose-gray dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{ __html: gig.description }}
            />
          </div>

          {/* What's Included */}
          {gig.includes && gig.includes.length > 0 && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                What's Included
              </h2>
              <ul className="space-y-2">
                {gig.includes.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Skills */}
          {gig.skills && gig.skills.length > 0 && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Skills & Expertise
              </h2>
              <div className="flex flex-wrap gap-2">
                {gig.skills.map((skill, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm rounded-md"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Portfolio */}
          {gig.portfolio && gig.portfolio.length > 0 && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Portfolio Examples
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {gig.portfolio.map((item, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {item.imageUrl && (
                      <div className="w-full h-48 overflow-hidden">
                        <img 
                          src={item.imageUrl} 
                          alt={item.description || `Portfolio item ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      {item.description && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                          {item.description}
                        </p>
                      )}
                      {item.videoUrl && (
                        <a 
                          href={item.videoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-accent hover:text-accent-dark flex items-center space-x-1"
                        >
                          <span>Watch Video</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Talent Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Service Provider
            </h3>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white text-lg font-medium">
                {gig.talent?.username?.[0].toUpperCase()}
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {gig.talent?.username}
                </div>
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <Star className="w-4 h-4 fill-current text-yellow-400" />
                  <span>{gig.talent?.stats?.rating?.average?.toFixed(1) || '0'}</span>
                  <span>({gig.talent?.stats?.rating?.count || 0} reviews)</span>
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center justify-between mb-1">
                <span>Gigs Completed:</span>
                <span className="font-medium">{gig.talent?.stats?.gigsCompleted || 0}</span>
              </div>
              <div className="flex items-center justify-between mb-1">
                <span>Success Rate:</span>
                <span className="font-medium">{gig.talent?.stats?.successRate || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Member Since:</span>
                <span className="font-medium">{new Date(gig.talent?.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Gig Stats */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Gig Statistics
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Orders</span>
                <span className="font-medium">{gig.orderCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Views</span>
                <span className="font-medium">{gig.views || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Rating</span>
                <span className="font-medium">{gig.rating?.average?.toFixed(1) || '0'} ({gig.rating?.count || 0})</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Posted</span>
                <span className="font-medium">{new Date(gig.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Form Modal */}
      {showOrderForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Order this Gig
            </h3>
            <form onSubmit={handleOrder} className="space-y-4">
              {/* Package Selection - Only show if basic/premium offers exist */}
              {gig.pricing && gig.pricing.basic && typeof gig.pricing.basic === 'object' && gig.pricing.basic.price && 
               gig.pricing.premium && typeof gig.pricing.premium === 'object' && gig.pricing.premium.price && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Package *
                  </label>
                  <select
                    value={orderData.package}
                    onChange={(e) => setOrderData(prev => ({ ...prev, package: e.target.value }))}
                    className="w-full input-field"
                    required
                  >
                    <option value="basic">
                      Basic - ${Number(gig.pricing.basic.price).toFixed(2)} ({gig.pricing.basic.deliveryTime?.value} {gig.pricing.basic.deliveryTime?.unit})
                    </option>
                    <option value="premium">
                      Premium - ${Number(gig.pricing.premium.price).toFixed(2)} ({gig.pricing.premium.deliveryTime?.value} {gig.pricing.premium.deliveryTime?.unit})
                    </option>
                  </select>
                  {orderData.package === 'basic' && gig.pricing.basic.pros && gig.pricing.basic.pros.length > 0 && (
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-1">What's included:</p>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                        {gig.pricing.basic.pros.map((pro, idx) => (
                          <li key={idx}>• {pro}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {orderData.package === 'premium' && gig.pricing.premium.pros && gig.pricing.premium.pros.length > 0 && (
                    <div className="mt-2 p-3 bg-accent/10 rounded">
                      <p className="text-xs font-medium text-accent mb-1">What's included:</p>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                        {gig.pricing.premium.pros.map((pro, idx) => (
                          <li key={idx}>• {pro}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Requirements *
                </label>
                <textarea
                  value={orderData.requirements}
                  onChange={(e) => setOrderData(prev => ({ ...prev, requirements: e.target.value }))}
                  rows={4}
                  className="w-full input-field"
                  placeholder="Describe your project requirements..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Budget ($) *
                </label>
                <input
                  type="number"
                  min="1"
                  value={orderData.budget}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                      setOrderData(prev => ({ ...prev, budget: value }));
                    }
                  }}
                  className="w-full input-field"
                  placeholder="Enter your budget"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timeline (days) *
                </label>
                <input
                  type="number"
                  min="1"
                  value={orderData.timeline}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                      setOrderData(prev => ({ ...prev, timeline: value }));
                    }
                  }}
                  className="w-full input-field"
                  placeholder="How many days do you need this completed?"
                  required
                />
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setShowOrderForm(false)}
                  className="btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={orderMutation.isLoading}
                  className="btn-primary flex-1"
                >
                  {orderMutation.isLoading ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GigDetailPage;